/**
 * Bulk Download Service
 *
 * Downloads large datasets via DuckDB API with format conversion.
 * Bypasses CF Worker limits for 10k+ row datasets.
 *
 * Flow:
 * 1. Request signed token from CF Worker unified query endpoint
 * 2. Use token to fetch directly from DuckDB API
 * 3. DuckDB API converts to requested format and returns file
 * 4. Trigger browser download
 *
 * @module lib/bulkDownload
 */

import type { QueryFormat } from '@loader/shared-types/duckdb';

import type { RpcClient } from '@/lib/rpc/client';

/**
 * Download format type
 * Excludes 'parquet' as it's not suitable for browser downloads
 */
export type DownloadFormat = Exclude<QueryFormat, 'parquet'>;

/**
 * Download progress status
 */
export type DownloadStatus = 'requesting' | 'downloading' | 'complete' | 'error';

/**
 * Options for bulk download
 */
export interface BulkDownloadOptions {
  /** Callback for progress updates */
  onProgress?: (status: DownloadStatus) => void;
}

/**
 * Extract filename from Content-Disposition header or generate default
 */
function getFilenameFromResponse(response: Response, format: DownloadFormat): string {
  const disposition = response.headers.get('Content-Disposition');
  if (disposition) {
    const filenameMatch = disposition.match(/filename="(.+)"/);
    if (filenameMatch) {
      return filenameMatch[1];
    }
  }

  // Default filename if not provided in response
  const extensions: Record<DownloadFormat, string> = {
    xlsx: '.xlsx',
    csv: '.csv',
    xml: '.xml',
    json: '.json',
  };
  return `download_${new Date().toISOString().split('T')[0]}${extensions[format]}`;
}

/**
 * Trigger browser download from blob
 */
export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Download table data in bulk
 *
 * @param rpc - RPC client instance
 * @param tableId - UUID of the table
 * @param versionId - UUID of the version to download
 * @param format - Download format (json, csv, xlsx, xml)
 * @param options - Optional callbacks
 *
 * @example
 * ```typescript
 * await downloadTableBulk(rpc, tableId, versionId, 'xlsx', {
 *   onProgress: (status) => console.log('Status:', status),
 * });
 * ```
 */
export async function downloadTableBulk(
  rpc: RpcClient,
  tableId: string,
  versionId: string,
  format: DownloadFormat,
  options?: BulkDownloadOptions
): Promise<void> {
  const { onProgress } = options || {};

  try {
    onProgress?.('requesting');

    // 1. Get signed token from unified query endpoint
    const tokenRes = await rpc.api.private.query.$post({
      json: {
        table: { tableId, versionId },
        format,
      },
    });

    if (!tokenRes.ok) {
      const error = await tokenRes.json().catch(() => ({ error: 'Failed to get download token' }));
      throw new Error((error as { error?: string }).error || 'Failed to get download token');
    }

    const tokenResponse = await tokenRes.json() as {
      token: string;
      url: string;
    };

    onProgress?.('downloading');

    // 2. Fetch from DuckDB API with signed token
    const response = await fetch(tokenResponse.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: tokenResponse.token }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Download failed');
      throw new Error(errorText || 'Download failed');
    }

    // 3. Get blob and trigger download
    const blob = await response.blob();
    const filename = getFilenameFromResponse(response, format);
    triggerDownload(blob, filename);

    onProgress?.('complete');
  } catch (error) {
    onProgress?.('error');
    throw error;
  }
}

/**
 * Download linked table data in bulk
 *
 * @param rpc - RPC client instance
 * @param linkedTableId - UUID of the linked table
 * @param format - Download format (json, csv, xlsx, xml)
 * @param tableVersions - Optional version overrides per table alias
 * @param options - Optional callbacks
 *
 * @example
 * ```typescript
 * await downloadLinkedTableBulk(rpc, linkedTableId, 'csv', {
 *   'orders': 'version-uuid-1',
 *   'products': 'version-uuid-2',
 * }, {
 *   onProgress: (status) => console.log('Status:', status),
 * });
 * ```
 */
export async function downloadLinkedTableBulk(
  rpc: RpcClient,
  linkedTableId: string,
  format: DownloadFormat,
  tableVersions?: Record<string, string>,
  options?: BulkDownloadOptions
): Promise<void> {
  const { onProgress } = options || {};

  try {
    onProgress?.('requesting');

    // 1. Get signed token from unified query endpoint
    const tokenRes = await rpc.api.private.query.$post({
      json: {
        linkedTable: { linkedTableId, tableVersions },
        format,
      },
    });

    if (!tokenRes.ok) {
      const error = await tokenRes.json().catch(() => ({ error: 'Failed to get download token' }));
      throw new Error((error as { error?: string }).error || 'Failed to get download token');
    }

    const tokenResponse = await tokenRes.json() as {
      token: string;
      url: string;
    };

    onProgress?.('downloading');

    // 2. Fetch from DuckDB API with signed token
    const response = await fetch(tokenResponse.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: tokenResponse.token }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Download failed');
      throw new Error(errorText || 'Download failed');
    }

    // 3. Get blob and trigger download
    const blob = await response.blob();
    const filename = getFilenameFromResponse(response, format);
    triggerDownload(blob, filename);

    onProgress?.('complete');
  } catch (error) {
    onProgress?.('error');
    throw error;
  }
}
